import './contact.css';
import { Button, Container, Form, FormGroup, Grid, Header, Icon, Input, Message, TextArea } from "semantic-ui-react";
import { useEffect, useRef, useState } from "react";
import GoogleCaptchaV2 from "./contact-form-captcha";

const MAX_ATTACHMENT_SIZE_BYTES = 4 * 1024 * 1024;
const MAX_ATTACHMENT_SIZE_LABEL = "4 MB";

const formatFileSize = (size) => {
    if (size < 1024) {
        return `${size} B`;
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const ContactForm = () => {

    const [emailFieldError, setEmailFieldError] = useState(false);
    const [firstNameFieldError, setFirstNameFieldError] = useState(false);
    const [lastNameFieldError, setLastNameFieldError] = useState(false);
    const [subjectFieldError, setSubjectFieldError] = useState(false);
    const [textFieldError, setTextFieldError] = useState(false);
    const [emailValue, setEmailValue] = useState("");
    const [firstNameValue, setFirstNameValue] = useState("");
    const [lastNameValue, setLastNameValue] = useState("");
    const [subjectValue, setSubjectValue] = useState("");
    const [textValue, setTextValue] = useState("");
    const [canSubmit, setCanSubmit] = useState(false);
    const [successMessageHidden, setSuccessMessageHidden] = useState(true);
    const [failureMessageHidden, setFailureMessageHidden] = useState(true);
    const [isCaptchaInvalid, setIsCaptchaInvalid] = useState(true);
    const [attachment, setAttachment] = useState(null);
    const [attachmentFieldError, setAttachmentFieldError] = useState("");
    const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
    const captchaRef = useRef(null);
    const fileInputRef = useRef(null);

    const valueFieldSetters = {
        firstName: setFirstNameValue,
        lastName: setLastNameValue,
        email: setEmailValue,
        subject: setSubjectValue,
        text: setTextValue
    }

    const setValue = (id, value) => {
        valueFieldSetters[id](value);
        setSuccessMessageHidden(true);
        setFailureMessageHidden(true);
    }

    const clearAttachment = () => {
        setAttachment(null);
        setAttachmentFieldError("");
        setIsAttachmentLoading(false);
        setSuccessMessageHidden(true);
        setFailureMessageHidden(true);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files[0];

        setSuccessMessageHidden(true);
        setFailureMessageHidden(true);

        if (!file) {
            clearAttachment();
            return;
        }

        if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
            setAttachment(null);
            setAttachmentFieldError(`Please choose a file smaller than ${MAX_ATTACHMENT_SIZE_LABEL}.`);
            event.target.value = "";
            return;
        }

        const reader = new FileReader();

        setIsAttachmentLoading(true);
        setAttachmentFieldError("");

        reader.onload = () => {
            const result = reader.result || "";
            const contentBase64 = result.toString().split(",")[1] || "";

            setAttachment({
                fileName: file.name,
                fileType: file.type || "application/octet-stream",
                fileSize: file.size,
                contentBase64
            });
            setIsAttachmentLoading(false);
        };

        reader.onerror = () => {
            setAttachment(null);
            setAttachmentFieldError("The file could not be attached. Please try another file or submit the form without an attachment.");
            setIsAttachmentLoading(false);
            event.target.value = "";
        };

        reader.readAsDataURL(file);
    };

    const validateFirstName = () => {
        if (firstNameValue && firstNameValue.length < 1){
            setFirstNameFieldError({
                content: "First name is mandatory",
                pointing: 'above'
            });
            setCanSubmit(false);
        } else {
            setFirstNameFieldError(false);
            setCanSubmit(true);
        }
    }

    const validateLastName = () => {
        if (lastNameValue && lastNameValue.length < 1){
            setLastNameFieldError({
                content: "Last name is mandatory",
                pointing: 'above'
            });
            setCanSubmit(false);
        } else {
            setLastNameFieldError(false);
            setCanSubmit(true);
        }
    }

    const validateEmail = () => {
        const pattern = /^([\w.-]+@([\w-]+\.)+[\w-]{2,6})?$/;
        if (emailValue && !pattern.test(emailValue)){
            setEmailFieldError({
                content: "Invalid email",
                pointing: 'above'
            });
            setCanSubmit(false);
        } else {
            setEmailFieldError(false);
            setCanSubmit(true);
        }
    }

    const validateSubject = () => {
        if (subjectValue && subjectValue.length < 1){
            setSubjectFieldError({
                content: "Subject is mandatory",
                pointing: 'above'
            });
            setCanSubmit(false);
        } else {
            setSubjectFieldError(false);
            setCanSubmit(true);
        }
    }

    const validateText = () => {
        if (textValue && textValue.length < 1){
            setTextFieldError({
                content: "Invalid message! Minimum 10 characters.",
                pointing: 'above'
            });
            setCanSubmit(false);
        } else {
            setTextFieldError(false);
            setCanSubmit(true);
        }
    }

    useEffect(() => {
        validateFirstName();
    }, [firstNameValue]);
    useEffect(() => {
        validateLastName();
    }, [lastNameValue]);
    useEffect(() => {
        validateEmail();
    }, [emailValue]);
    useEffect(() => {
        validateSubject();
    }, [subjectValue]);
    useEffect(() => {
        validateText();
    }, [textValue]);

    const onSubmit = (event) => {
        event.preventDefault();

        if (canSubmit
            && !isAttachmentLoading
            && firstNameValue && lastNameValue && emailValue && subjectValue && textValue
            && captchaRef?.current?.getValue()) {
            const token = captchaRef.current.getValue();
            submit(token);

            setFirstNameValue("");
            setLastNameValue("")
            setEmailValue("");
            setSubjectValue("");
            setTextValue("");
            clearAttachment();
        }
    }

    const submit = (token) => {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                firstName: firstNameValue,
                lastName: lastNameValue,
                email: emailValue,
                subject: subjectValue,
                text: textValue,
                attachment: attachment ? {
                    fileName: attachment.fileName,
                    fileType: attachment.fileType,
                    fileSize: attachment.fileSize,
                    contentBase64: attachment.contentBase64
                } : null,
                token: token
            })
        };
        fetch("https://97og0gpac8.execute-api.eu-west-2.amazonaws.com/v1/contact-us", requestOptions)
            .then(response => {
                console.log(response.json());
                if (response.status === 200) {
                    setSuccessMessageHidden(false);
                } else {
                    setFailureMessageHidden(false);
                }
            })
            .catch(err => {
                console.error(err);
                setFailureMessageHidden(false);
            });
    }

    return (
        <Form className='contact-form'
              onSubmit={onSubmit}
              noValidate
              success
        >
            <FormGroup widths='equal'>
                <Form.Field
                    id='firstName'
                    required
                    control={Input}
                    label="First name"
                    error={firstNameFieldError}
                    value={firstNameValue}
                    onChange={event => setValue(event.target.id, event.target.value)}
                />
                <Form.Field
                    id='lastName'
                    required
                    control={Input}
                    label="Last name"
                    error={lastNameFieldError}
                    value={lastNameValue}
                    onChange={event => setValue(event.target.id, event.target.value)}
                />
            </FormGroup>
            <Form.Field
                id='email'
                required
                control={Input}
                label="Email"
                error={emailFieldError}
                value={emailValue}
                onChange={event => setValue(event.target.id, event.target.value)}
            />
            <Form.Field
                id='subject'
                control={Input}
                label="Subject"
                error={subjectFieldError}
                value={subjectValue}
                onChange={event => setValue(event.target.id, event.target.value)}
            />
            <Form.Field
                id='text'
                required
                control={TextArea}
                label="Message"
                error={textFieldError}
                value={textValue}
                onChange={event => setValue(event.target.id, event.target.value)}
            />
            <div className="contact-attachment">
                <label className="contact-attachment-label" htmlFor="attachment">
                    Attachment (optional)
                </label>
                <div className="contact-attachment-controls">
                    <input
                        ref={fileInputRef}
                        id="attachment"
                        type="file"
                        className="contact-file-input"
                        onChange={handleAttachmentChange}
                    />
                    <Button
                        as="label"
                        htmlFor="attachment"
                        basic
                        className="contact-file-button"
                    >
                        <Icon name="paperclip" />
                        Choose File
                    </Button>
                    {isAttachmentLoading &&
                        <span className="contact-attachment-status">
                            Preparing file...
                        </span>
                    }
                    {attachment && !isAttachmentLoading &&
                        <span className="contact-attachment-selected">
                            <Icon name="file outline" />
                            <span>
                                {attachment.fileName} ({formatFileSize(attachment.fileSize)})
                            </span>
                            <Button
                                type="button"
                                icon="close"
                                aria-label="Remove attachment"
                                className="contact-file-remove"
                                onClick={clearAttachment}
                            />
                        </span>
                    }
                </div>
                <p className="contact-attachment-help">
                    Any single file type, up to {MAX_ATTACHMENT_SIZE_LABEL}.
                </p>
                {attachmentFieldError &&
                    <Message
                        negative
                        size="small"
                        className="contact-attachment-error"
                        content={attachmentFieldError}
                    />
                }
            </div>
            <GoogleCaptchaV2
                captchaRef={captchaRef}
                isCaptchaInvalid={isCaptchaInvalid}
                setIsCaptchaInvalid={setIsCaptchaInvalid}/>
            <Button primary size='massive' className="contact-form-button" disabled={isCaptchaInvalid || isAttachmentLoading}>
                Submit
            </Button>
            <Message
                hidden={successMessageHidden}
                success
                header="Request sent"
                content="We will contact you soon"
            />
            <Message
                hidden={failureMessageHidden}
                negative
                header="Failed to send the request"
                content="Please try again later or contact us using the details on this page."
            />
        </Form>
    );
};

const Contact = () => {
    return (
        <Container className="contact">
            <Grid verticalAlign='middle' doubling columns={2}>
                <Grid.Column>
                    <Header
                        as='h3'
                        content='Let’s Work Together'
                        className='contact-h3'
                    />
                    <p className="contact-description">
                        We’re always looking for new opportunities and are comfortable working internationally.
                        Please get in touch and one of our project managers will contact you about beginning the
                        proposal process.
                    </p>
                    <hr className="contact-details-hr"/>
                    <div className="contact-details-block">
                        <div className="contact-person">
                            <p className="contact-details">
                                <a href="tel:+441604217969" className="contact-link">
                                    +44 1604 217969
                                </a>
                            </p>
                            <p className="contact-details">
                                <a href="tel:+447850048527" className="contact-link">
                                    +44 7850 048527
                                </a>
                            </p>
                            <p className="contact-details">
                                <a href="mailto:info@visgroup.co.uk" className="contact-link">
                                    info@@visgroup.co.uk
                                </a>
                            </p>
                            <p className="contact-details">
                                <a href="https://visgroup.co.uk" target="_blank" rel="noreferrer" className="contact-link">
                                    visgroup.co.uk
                                </a>
                            </p>
                        </div>
                    </div>
                </Grid.Column>
                <Grid.Column>
                    <ContactForm/>
                </Grid.Column>
            </Grid>
        </Container>
    );
};

export default Contact;
